import type { OrderItem } from "../types";

/**
 * HA-657 — the VAT-inclusive way to read an order's tax. The server's `tax` is the VAT the total CONTAINS, and
 * the total is the sum of the lines; an order rung before that had its tax added ON TOP at the order's rate
 * (total = subtotal + tax). A line-by-line mix of the two is possible on one old order, so the split is read from
 * the figures themselves: whatever the total exceeds the subtotal by was added on top, the rest of the tax is
 * included in the prices.
 *
 * HA-357: the venue's service charge is in the total too, and it is not tax (no VAT on it). So it's taken out
 * before the rest is read; otherwise a 12.5% charge on a VAT-inclusive order would show as tax added on top.
 */
export function taxBreakdown(order: {
	subtotal: { value: number };
	tax: number;
	total: { value: number };
	serviceChargeAmount?: { value: number } | null;
}) {
	const charge = order.serviceChargeAmount?.value ?? 0;
	const onTop = Math.max(0, order.total.value - order.subtotal.value - charge);
	const included = Math.max(0, order.tax - onTop);
	return { onTop, included };
}

/** HA-357 — the venue's service charge settings as the till holds them (the order's snapshot, as the server sends it). */
export interface ServiceChargeSettings {
	enabled: boolean;
	type: "Percentage" | "Fixed";
	value: number;
}

/**
 * HA-357 — the server's rule (ServiceChargeCalc), for the till's own running total: a percentage of the subtotal,
 * rounded half-up to the minor unit; a fixed amount as it is; nothing when the charge is off or the order has none.
 * No discount is taken off first here: a discount is not in the till's running total either.
 */
export function serviceChargeMinor(
	serviceCharge: ServiceChargeSettings | null | undefined,
	subtotalMinor: number,
): number {
	if (!serviceCharge?.enabled) return 0;
	const charge =
		serviceCharge.type === "Fixed"
			? serviceCharge.value * 100
			: (Math.max(0, subtotalMinor) * serviceCharge.value) / 100;
	return Math.round(charge);
}

/**
 * HA-357 slice C (AC9) — the charge the order carries now: its snapshot, or nothing once a staff member has taken it
 * off. The till's running total reads this, so a removed charge is out of the total until the server answers again.
 */
export function appliedServiceCharge(order: {
	serviceCharge?: ServiceChargeSettings | null;
	serviceChargeRemoved?: boolean;
}): ServiceChargeSettings | null {
	if (order.serviceChargeRemoved) return null;
	return order.serviceCharge ?? null;
}

/**
 * HA-849 — the server's formula (OrderMoney), for the moment before a line write answers: a line is
 * (unit price + its options) × quantity; tax is the rate on the subtotal, exclusive, rounded half-up to
 * the minor unit. HA-357: the service charge is added on top of that, the same as the server does, so the
 * amount due is the charged one. The server's own figures replace these on every response (withServerFigures).
 */
export function orderTotals(
	items: OrderItem[],
	taxRatePercent: number,
	serviceCharge?: ServiceChargeSettings | null,
	comped?: ReadonlySet<string>,
) {
	const subtotal = items.reduce((sum, item) => {
		// HA-658 — a comped line is charged £0, so it adds nothing here, tax and service charge included. Only the placeholder
		// does this: the server's figures replace it on every response.
		if (comped?.has(item.id)) return sum;
		let unit = item.price.value;
		for (const group of item.selectedOptions ?? []) {
			for (const selectedItem of group.selectedItems) {
				unit += selectedItem.priceImpact.value;
			}
		}
		return sum + unit * item.quantity;
	}, 0);
	const tax = Math.round((subtotal * taxRatePercent) / 100);
	const serviceChargeAmount = serviceChargeMinor(serviceCharge, subtotal);
	return {
		subtotal,
		tax,
		serviceChargeAmount,
		total: subtotal + tax + serviceChargeAmount,
	};
}

/**
 * HA-357 slice C — the till's copy of an order takes the charge the server holds for it: the venue's snapshot, whether a
 * staff member has taken it off, and the amount. A new order on the till has none of these until the server has it.
 */
export function withServerCharge<
	T extends {
		serviceCharge?: ServiceChargeSettings | null;
		serviceChargeRemoved?: boolean;
		serviceChargeAmount?: { value: number; currency: string } | null;
	},
>(
	local: T,
	server: {
		serviceCharge?: ServiceChargeSettings | null;
		serviceChargeRemoved?: boolean;
		serviceChargeAmount?: { value: number; currency: string } | null;
	},
): T {
	return {
		...local,
		serviceCharge: server.serviceCharge ?? null,
		serviceChargeRemoved: server.serviceChargeRemoved ?? false,
		serviceChargeAmount:
			server.serviceChargeAmount ?? local.serviceChargeAmount,
	};
}
